import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';

// Password strength rule checks
const rules = [
  { id: 'len',   label: 'At least 8 characters',                test: (p) => p.length >= 8 },
  { id: 'upper', label: 'At least one uppercase letter (A-Z)',  test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'At least one lowercase letter (a-z)',  test: (p) => /[a-z]/.test(p) },
  { id: 'num',   label: 'At least one number (0-9)',            test: (p) => /\d/.test(p) },
];

function PasswordRule({ passed, label }) {
  return (
    <li className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-slate-400'}`}>
      {passed
        ? <CheckCircle2 size={13} className="flex-shrink-0" />
        : <XCircle size={13} className="flex-shrink-0" />}
      {label}
    </li>
  );
}

function PasswordInput({ id, label, value, onChange, show, onToggle, placeholder, disabled }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="pr-10"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function AccountSecurity() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword]   = useState('');
  const [newPassword,     setNewPassword]        = useState('');
  const [confirmPassword, setConfirmPassword]    = useState('');
  const [showCurrent,     setShowCurrent]        = useState(false);
  const [showNew,         setShowNew]            = useState(false);
  const [showConfirm,     setShowConfirm]        = useState(false);
  const [loading,         setLoading]            = useState(false);

  // Guard: must be authenticated (belt-and-suspenders beyond route protection)
  if (!user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-slate-500">Access denied.</p>
        </div>
      </Layout>
    );
  }

  const isSuperAdmin = user?.role === 'super_admin';

  const rulePassed = rules.map(r => ({ ...r, passed: r.test(newPassword) }));
  const allRulesPassed = rulePassed.every(r => r.passed);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!currentPassword) { toast.error('Please enter your current password'); return; }
    if (!allRulesPassed)   { toast.error('New password does not meet all requirements'); return; }
    if (!passwordsMatch)   { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      await authAPI.changePassword({
        current_password: currentPassword,
        new_password:     newPassword,
        confirm_password: confirmPassword,
      });

      toast.success('Password changed. Logging you out now…', { duration: 3000 });

      // Clear form immediately so credentials aren't left in memory
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Wait briefly so the user sees the success toast, then force re-login
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);

    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update password. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-lg space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Account Security
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isSuperAdmin
              ? 'Change your Super Admin password. All active sessions will be invalidated.'
              : 'Change your account password. You will be signed out after saving.'}
          </p>
        </div>

        {/* Change Password card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck size={20} className="text-orange-500" />
              Change Password
            </CardTitle>
            <CardDescription>
              After saving, you will be signed out and must log in with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
              {/* Current Password */}
              <PasswordInput
                id="current-password"
                label="Current Password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                show={showCurrent}
                onToggle={() => setShowCurrent(v => !v)}
                placeholder="Enter your current password"
                disabled={loading}
              />

              {/* New Password */}
              <PasswordInput
                id="new-password"
                label="New Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                show={showNew}
                onToggle={() => setShowNew(v => !v)}
                placeholder="Enter your new password"
                disabled={loading}
              />

              {/* Strength rules — shown only when user starts typing */}
              {newPassword.length > 0 && (
                <ul className="space-y-1 pl-1">
                  {rulePassed.map(r => <PasswordRule key={r.id} passed={r.passed} label={r.label} />)}
                </ul>
              )}

              {/* Confirm New Password */}
              <PasswordInput
                id="confirm-password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                show={showConfirm}
                onToggle={() => setShowConfirm(v => !v)}
                placeholder="Re-enter your new password"
                disabled={loading}
              />

              {/* Match indicator */}
              {confirmPassword.length > 0 && (
                <p className={`text-xs flex items-center gap-1.5 ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordsMatch
                    ? <><CheckCircle2 size={13} /> Passwords match</>
                    : <><XCircle size={13} /> Passwords do not match</>}
                </p>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading || !currentPassword || !allRulesPassed || !passwordsMatch}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 mt-2"
                data-testid="change-password-btn"
              >
                {loading
                  ? <><Loader2 size={16} className="animate-spin mr-2" /> Updating…</>
                  : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
